// @ts-nocheck
// Agent Portal (/cti2) — a fixed, minimal-height strip above the live-call
// card stack (LiveCallStack.tsx) showing the agent's Assigned Groups,
// Active Campaigns, and Inbound Calling Agent Schedule. None of this
// existed in cti2 before (AgentPortalPage.tsx's own header comment
// explicitly lists it as a deliberate trim) — the protected IdleScreen.tsx
// has the same three facts spread across three separate always-expanded
// dashboard-grid panels; here they're one collapsed one-liner each, so the
// space above the customer card stays minimal until the agent asks for
// more (an agent can belong to any number of groups, so "always expanded"
// doesn't scale down here the way it does in a full customizable grid).
//
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { clearApiCallShared, getApiCallShared } from '@/service/sharedGet';
import { API_AGENT_SCHEDULE, CALL_GROUP_TOGGLE_AGENT, API_AGENT_DASHBOARD_CAMPAIGN_TOGGLE } from '@/service/url';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useModuleOps } from '../hooks/useModuleOps';
import { HAIRLINE } from '../team/teamUi';
import { Switch } from '@/components/ui/switch';

// Same day/time-window comparison IdleScreen.tsx's panels.schedule and
// panels.groups both already use (isInSchedule) — reimplemented fresh
// rather than imported, since IdleScreen.tsx is one of the four protected
// production files and doesn't export it.
function isInSchedule(schedule) {
  if (!schedule?.start_time || !schedule?.end_time) return null;
  const now = new Date();
  const days = Array.isArray(schedule.week_days) ? schedule.week_days : [];
  if (days.length && !days.includes(now.toLocaleString('en-US', { weekday: 'long' }))) return false;
  const [sh, sm] = String(schedule.start_time).split(':').map(Number);
  const [eh, em] = String(schedule.end_time).split(':').map(Number);
  const startMin = sh * 60 + (sm || 0);
  const endMin = eh * 60 + (em || 0);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return startMin > endMin
    ? nowMin >= startMin || nowMin < endMin // overnight window
    : nowMin >= startMin && nowMin < endMin;
}

function StatusDot({ active }) {
  return (
    <span className={`inline-block h-1.5 w-1.5 rounded-full ${active ? 'bg-success' : 'bg-muted-foreground/40'}`} />
  );
}

function InSchedulePill({ schedule }) {
  const inSchedule = isInSchedule(schedule);
  if (inSchedule === null) return null;
  return (
    <span
      className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
        inSchedule ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
      }`}
    >
      {inSchedule ? 'In' : 'Out'}
    </span>
  );
}

function ScheduleBlock({ icon, label, schedule }) {
  const inSchedule = isInSchedule(schedule);
  return (
    <div className={`rounded-lg border ${HAIRLINE} bg-muted/30 px-2.5 py-2`}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {icon} {label}
        </span>
        {inSchedule !== null && (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
              inSchedule ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'
            }`}
          >
            {inSchedule ? 'In' : 'Out'}
          </span>
        )}
      </div>
      {schedule?.start_time && schedule?.end_time ? (
        <>
          <div className="font-mono text-xs font-semibold text-foreground">
            {schedule.start_time}–{schedule.end_time}
          </div>
          {Array.isArray(schedule.week_days) && schedule.week_days.length > 0 && (
            <div className="mt-0.5 text-[10.5px] text-muted-foreground">{schedule.week_days.join(', ')}</div>
          )}
        </>
      ) : (
        <div className="text-[10.5px] text-muted-foreground">Not assigned</div>
      )}
    </div>
  );
}

function ContextChip({ label, count, pulse, children, width = 'w-72' }) {
  // A popover trigger's "selected" state maps naturally onto whether the
  // popover is actually open — embossed for as long as it's open, back to
  // normal the moment it closes, same lasting on/off feedback the other
  // dashboard tabs give instead of a press effect that vanished on mouse-up
  // before you could tell the click had registered.
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`ap-tab-press inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium text-foreground transition-all ${open ? 'ap-tab-emboss' : ''}`}
        >
          {label}
          {count != null ? (
            <span
              className={`rounded-full px-1.5 text-[10px] font-semibold ${pulse ? 'bg-warning/20 text-warning' : 'bg-primary/15 text-primary'}`}
            >
              {count}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      {/* z-[1100]: same override every Radix portal needs to clear
        * .ap-shell's z-[1000] inside cti2 — see FilterBarPill.tsx. */}
      <PopoverContent align="start" sideOffset={6} className={`z-[1100] ${width} p-0`}>
        {children}
      </PopoverContent>
    </Popover>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="truncate text-xs font-medium text-foreground">{value}</span>
    </div>
  );
}

function EmptyRow({ children }) {
  return <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">{children}</div>;
}

function SearchField({ value, onChange, placeholder }) {
  return (
    <div className={`flex items-center gap-1.5 border-b ${HAIRLINE} bg-muted/20 px-2.5 py-1.5`}>
      <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent text-[11px] text-foreground outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

function PopoverHead({ title, count }) {
  return (
    <div className="flex items-center justify-between px-2.5 pb-1.5 pt-2">
      <span className="text-[11px] font-semibold text-foreground">{title}</span>
      {count != null && <span className="text-[10px] text-muted-foreground">{count}</span>}
    </div>
  );
}

export default function AgentContextStrip({
  agentId,
  userData,
  campaigns = [],
  className = 'mb-2 flex flex-shrink-0 flex-wrap items-center gap-1.5',
  onGroupsClick,
  onCampaignsClick,
}: any) {
  // Groups/Campaigns jump to a section further down instead of opening
  // like Schedule's popover, so there's no natural "open/closed" signal to
  // key their emboss off — this tracks "which of these two was clicked
  // last" instead, so the click leaves a lasting selected look (until the
  // other one is clicked) rather than a press flash that's gone by the
  // time you look at it.
  const [activeChip, setActiveChip] = useState(null);
  // Schedule renders last (after the dashboard tabs), same portal
  // technique AgentDashboardView.tsx already uses for
  // ap-topbar-tabs-slot — this component still fetches/owns the schedule
  // data itself, only its Schedule chip's DOM position moves via portal.
  const [scheduleSlotNode, setScheduleSlotNode] = useState(null);
  useEffect(() => {
    const check = () => {
      setScheduleSlotNode((prev) => (
        prev && prev.isConnected ? prev : document.getElementById('ap-topbar-schedule-slot')
      ));
    };
    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, []);
  const [groups, setGroups] = useState<any[]>([]);
  const [scheduleData, setScheduleData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [togglingGroupId, setTogglingGroupId] = useState(null);
  const [togglingCampaignId, setTogglingCampaignId] = useState(null);
  const [campaignOverrides, setCampaignOverrides] = useState<Record<string, boolean>>({});

  const moduleOps = useModuleOps();
  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const canReadCallGroup = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CALL_GROUP, 'read');
  const canEditCallGroup = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CALL_GROUP, 'edit');
  const canReadCampaigns = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CAMPAIGNS, 'read');
  const canEditCampaigns = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CAMPAIGNS, 'edit');

  useEffect(() => {
    if (!agentId) return;
    let mounted = true;
    setLoading(true);
    getApiCallShared(`${API_AGENT_SCHEDULE}/${agentId}`)
      .then((resp: any) => {
        if (!mounted) return;
        setGroups(Array.isArray(resp?.groups) ? resp.groups : []);
        setScheduleData(resp?.scheduleData || null);
      })
      .catch(() => {
        if (mounted) { setGroups([]); setScheduleData(null); }
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [agentId]);

  const campaignsResolved = useMemo(
    () =>
      campaigns.map((c) => ({
        ...c,
        agent_campaign_active:
          campaignOverrides[c.campaign_id ?? c.id] ?? c.agent_campaign_active !== false,
      })),
    [campaigns, campaignOverrides],
  );
  const activeCampaigns = campaignsResolved.filter((c) => c.agent_campaign_active !== false);
  const scheduleInNow = isInSchedule(scheduleData);

  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => String(g.group_name || '').toLowerCase().includes(q));
  }, [groups, groupSearch]);

  const filteredCampaigns = useMemo(() => {
    const q = campaignSearch.trim().toLowerCase();
    if (!q) return campaignsResolved;
    return campaignsResolved.filter((c) => String(c.campaign_name || '').toLowerCase().includes(q));
  }, [campaignsResolved, campaignSearch]);

  const agentRecordId = userData?.agentData?.id;

  const handleToggleGroup = async (grp, nextActive) => {
    const groupId = grp?.group_id;
    if (!groupId || !agentRecordId || togglingGroupId) return;
    setTogglingGroupId(groupId);
    try {
      await postApiCall(`${CALL_GROUP_TOGGLE_AGENT}/${groupId}`, {
        agent_id: agentRecordId,
        is_active: nextActive,
      });
      setGroups((prev) => prev.map((g) => (g.group_id === groupId ? { ...g, is_active: nextActive } : g)));
      clearApiCallShared();
      toast.success(`Call group turned ${nextActive ? 'on' : 'off'}`);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to toggle call group.');
    } finally {
      setTogglingGroupId(null);
    }
  };

  const handleToggleCampaign = async (campaign, nextActive) => {
    const campaignId = campaign?.campaign_id ?? campaign?.id;
    if (!campaignId || !agentRecordId || togglingCampaignId) return;
    setTogglingCampaignId(campaignId);
    try {
      await postApiCall(API_AGENT_DASHBOARD_CAMPAIGN_TOGGLE, {
        agent_id: agentRecordId,
        campaign_id: campaignId,
        is_active: nextActive,
      });
      setCampaignOverrides((prev) => ({ ...prev, [campaignId]: nextActive }));
      toast.success(`Campaign turned ${nextActive ? 'on' : 'off'}`);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to toggle campaign.');
    } finally {
      setTogglingCampaignId(null);
    }
  };

  return (
    <div className={className}>
      {!canReadCallGroup ? null : onGroupsClick ? (
        // Groups now behaves like the dashboard's own section tabs (click
        // → jump to a full section below) instead of opening a popover —
        // per request ("jaise baki tab pr click krne pr niche container m
        // show krte hai, vaise Groups ko bhi"). The list itself moved into
        // AgentDashboardView's "groups" section (see AgentGroupsSection).
        <button
          type="button"
          onClick={() => { setActiveChip('groups'); onGroupsClick(); }}
          className={`ap-tab-press inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium text-foreground transition-all ${activeChip === 'groups' ? 'ap-tab-emboss' : ''}`}
        >
          Groups
          {!loading && groups.length ? (
            <span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">
              {groups.length}
            </span>
          ) : null}
        </button>
      ) : (
        <ContextChip label="Groups" count={loading ? null : groups.length}>
          {!canReadCallGroup ? (
            <EmptyRow>ACL permission missing for Call Groups.</EmptyRow>
          ) : (
            <>
              {groups.length > 3 && (
                <SearchField value={groupSearch} onChange={setGroupSearch} placeholder="Search groups…" />
              )}
              <PopoverHead title="Assigned groups" count={groups.length || undefined} />
              <div className="ap-noscrollbar max-h-72 space-y-1 overflow-y-auto px-1.5 pb-1.5">
                {filteredGroups.length === 0 ? (
                  <EmptyRow>{loading ? 'Loading…' : groupSearch ? 'No groups match' : 'No groups assigned'}</EmptyRow>
                ) : (
                  filteredGroups.map((g) => (
                    <div key={g.group_id} className="space-y-1.5 rounded-md px-1.5 py-1.5 hover:bg-muted/40">
                      <div className="flex items-center gap-2">
                        <StatusDot active={g.is_active !== false} />
                        <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                          {g.group_name || `Group #${g.group_id}`}
                        </span>
                        <InSchedulePill schedule={g.schedule || g.group_schedule} />
                        {canEditCallGroup ? (
                          togglingGroupId === g.group_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          ) : (
                            <Switch
                              checked={g.is_active !== false}
                              onCheckedChange={(checked) => handleToggleGroup(g, checked)}
                              className="scale-75"
                            />
                          )
                        ) : null}
                      </div>
                      <div className="space-y-1.5 rounded-lg border border-border/60 bg-muted/20 p-2 pl-3.5">
                        <DetailRow label="Owner" value={g.group_owner} />
                        <DetailRow label="Priority" value={g.priority} />
                        <DetailRow label="Status" value={g.is_active !== false ? 'Active' : 'Inactive'} />
                        <div className="grid grid-cols-2 gap-1.5 pt-0.5">
                          <ScheduleBlock icon="🧩" label="Group" schedule={g.group_schedule} />
                          <ScheduleBlock icon="👤" label="You" schedule={g.schedule} />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </ContextChip>
      )}

      {!canReadCampaigns ? null : onCampaignsClick ? (
        // Same shift as Groups above — jumps to a full section below
        // instead of opening a popover (see AgentCampaignsSection).
        <button
          type="button"
          onClick={() => { setActiveChip('campaigns'); onCampaignsClick(); }}
          className={`ap-tab-press inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium text-foreground transition-all ${activeChip === 'campaigns' ? 'ap-tab-emboss' : ''}`}
        >
          Campaigns
          {campaigns.length ? (
            <span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">
              {activeCampaigns.length}
            </span>
          ) : null}
        </button>
      ) : (
        <ContextChip label="Campaigns" count={campaigns.length ? activeCampaigns.length : null}>
          {!canReadCampaigns ? (
            <EmptyRow>ACL permission missing for Campaigns.</EmptyRow>
          ) : (
            <>
              {campaigns.length > 3 && (
                <SearchField value={campaignSearch} onChange={setCampaignSearch} placeholder="Search campaigns…" />
              )}
              <PopoverHead title="Active campaigns" count={campaigns.length || undefined} />
              <div className="ap-noscrollbar max-h-72 space-y-1 overflow-y-auto px-1.5 pb-1.5">
                {filteredCampaigns.length === 0 ? (
                  <EmptyRow>{campaignSearch ? 'No campaigns match' : 'No active campaigns'}</EmptyRow>
                ) : (
                  filteredCampaigns.map((c) => (
                    <div
                      key={c.campaign_id ?? c.id}
                      className="flex items-center gap-2 rounded-md px-1.5 py-1.5 text-xs hover:bg-muted/40"
                    >
                      <StatusDot active={c.agent_campaign_active !== false} />
                      <span className="min-w-0 flex-1 truncate text-foreground">
                        {c.campaign_name || `Campaign #${c.campaign_id ?? c.id}`}
                      </span>
                      {canEditCampaigns ? (
                        togglingCampaignId === (c.campaign_id ?? c.id) ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        ) : (
                          <Switch
                            checked={c.agent_campaign_active !== false}
                            onCheckedChange={(checked) => handleToggleCampaign(c, checked)}
                            className="scale-75"
                          />
                        )
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </ContextChip>
      )}

      {scheduleSlotNode && createPortal(
        <ContextChip label="Schedule" width="w-64">
          <div className="p-3">
            {!scheduleData?.start_time ? (
              <EmptyRow>{loading ? 'Loading…' : 'No schedule configured'}</EmptyRow>
            ) : (
              <div className="space-y-2.5">
                <div className="flex flex-col items-center gap-1 rounded-xl bg-gradient-to-b from-primary/10 to-transparent py-3 text-center">
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                      scheduleInNow ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'
                    }`}
                  >
                    {scheduleInNow ? 'In Schedule' : 'Out of Schedule'}
                  </span>
                  <div className="mt-1 font-mono text-base font-bold text-foreground">
                    {scheduleData.start_time}–{scheduleData.end_time}
                  </div>
                </div>
                {Array.isArray(scheduleData.week_days) && scheduleData.week_days.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1">
                    {scheduleData.week_days.map((d) => (
                      <span
                        key={d}
                        className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                      >
                        {String(d).slice(0, 3)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </ContextChip>,
        scheduleSlotNode,
      )}
    </div>
  );
}
